/**
 * Keyboard + mouse → PlayerInput. Pointer lock on the canvas; the mouse looks
 * and picks who and which line a strike goes for (ADR-003), limbs are
 * buttons, arcs (R, T) and zones (F, Shift) are modifiers.
 *
 * Screen directions, once: the world is right-handed with y up and a fighter
 * at yaw `a` faces `(sin a, 0, cos a)`, so the camera's right is
 * `(-cos a, 0, sin a)` and yaw grows counter-clockwise on screen. Mouse right
 * therefore *decreases* yaw. Strafing needs no such correction: `moveX` is
 * "the fighter's right" and `rotateY` puts it on the right of the screen.
 */
import { Buttons, emptyInput, type PlayerInput, yawFromRadians } from "@meleeroyale/sim";

/**
 * Key code → action bit. The simulation never sees a key. Owner layout,
 * 2026-09-25: F and Shift pick the zone (a punch to the body, a kick to the
 * head or the legs; cover the body, or check), R and T the arc; the bottom
 * row evades (slip, crouch, slip, step). Never Ctrl: Ctrl + W closes the tab.
 */
const KEY_BUTTONS: Record<string, number> = {
  Space: Buttons.GUARD,
  KeyF: Buttons.AIM_ALT,
  ShiftLeft: Buttons.AIM_LOW,
  ShiftRight: Buttons.AIM_LOW,
  KeyR: Buttons.ARC_AROUND,
  KeyT: Buttons.ARC_RISING,
  KeyQ: Buttons.LEG_L,
  KeyE: Buttons.LEG_R,
  KeyZ: Buttons.LEAN_L,
  KeyX: Buttons.DUCK,
  KeyC: Buttons.LEAN_R,
  KeyV: Buttons.STEP,
  Period: Buttons.STANCE,
};

const RADIANS_TO_DEGREES = 57.29577951308232;
const SETTINGS_KEY = "melee.look";

export interface LookSettings {
  /** Mouse down looks up. */
  invertY: boolean;
}

export function loadLookSettings(): LookSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<LookSettings>;
      return { invertY: parsed.invertY === true };
    }
  } catch {
    /* private mode or blocked storage: defaults are fine */
  }
  return { invertY: false };
}

function saveLookSettings(settings: LookSettings): void {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    /* nothing to do: the setting just will not persist */
  }
}

export class Controls {
  yaw = 0;
  pitch = 0;
  sensitivity = 0.0022;
  locked = false;
  settings: LookSettings = loadLookSettings();
  private keys = new Set<string>();
  private buttons = 0;
  private readonly element: HTMLElement;
  private readonly onLockChange: (locked: boolean) => void;

  constructor(element: HTMLElement, onLockChange: (locked: boolean) => void, initialYaw = 0) {
    this.element = element;
    this.onLockChange = onLockChange;
    this.yaw = initialYaw;
    element.addEventListener("click", this.requestLock);
    element.addEventListener("contextmenu", prevent);
    document.addEventListener("pointerlockchange", this.lockChanged);
    document.addEventListener("mousemove", this.mouseMove);
    document.addEventListener("mousedown", this.mouseDown);
    document.addEventListener("mouseup", this.mouseUp);
    document.addEventListener("keydown", this.keyDown);
    document.addEventListener("keyup", this.keyUp);
    window.addEventListener("blur", this.releaseAll);
  }

  dispose(): void {
    this.element.removeEventListener("click", this.requestLock);
    this.element.removeEventListener("contextmenu", prevent);
    document.removeEventListener("pointerlockchange", this.lockChanged);
    document.removeEventListener("mousemove", this.mouseMove);
    document.removeEventListener("mousedown", this.mouseDown);
    document.removeEventListener("mouseup", this.mouseUp);
    document.removeEventListener("keydown", this.keyDown);
    document.removeEventListener("keyup", this.keyUp);
    window.removeEventListener("blur", this.releaseAll);
    if (document.pointerLockElement === this.element) document.exitPointerLock();
  }

  setInvertY(invertY: boolean): void {
    this.settings = { ...this.settings, invertY };
    saveLookSettings(this.settings);
  }

  requestLock = (): void => {
    if (document.pointerLockElement !== this.element) {
      this.element.requestPointerLock().catch?.(() => {});
    }
  };

  sample(tick: number): PlayerInput {
    const input = emptyInput(tick);
    const right = (this.keys.has("KeyD") ? 1 : 0) - (this.keys.has("KeyA") ? 1 : 0);
    const forward = (this.keys.has("KeyW") ? 1 : 0) - (this.keys.has("KeyS") ? 1 : 0);
    input.moveX = right * 127;
    input.moveY = forward * 127;
    input.yaw = yawFromRadians(this.yaw);
    input.pitch = Math.round(this.pitch);
    input.buttons = this.buttons;
    return input;
  }

  private lockChanged = (): void => {
    this.locked = document.pointerLockElement === this.element;
    if (!this.locked) this.releaseAll();
    this.onLockChange(this.locked);
  };

  private mouseMove = (e: MouseEvent): void => {
    if (!this.locked) return;
    // Mouse right turns the view right: yaw grows counter-clockwise on screen.
    this.yaw -= e.movementX * this.sensitivity;
    const up = this.settings.invertY ? e.movementY : -e.movementY;
    this.pitch += up * this.sensitivity * RADIANS_TO_DEGREES;
    if (this.pitch > 89) this.pitch = 89;
    if (this.pitch < -89) this.pitch = -89;
  };

  private mouseDown = (e: MouseEvent): void => {
    if (!this.locked) return;
    if (e.button === 0) this.buttons |= Buttons.ARM_L;
    if (e.button === 2) this.buttons |= Buttons.ARM_R;
  };

  private mouseUp = (e: MouseEvent): void => {
    if (e.button === 0) this.buttons &= ~Buttons.ARM_L;
    if (e.button === 2) this.buttons &= ~Buttons.ARM_R;
  };

  private keyDown = (e: KeyboardEvent): void => {
    if (!this.locked) return;
    if (e.code === "Tab" || e.code === "Space") e.preventDefault();
    this.keys.add(e.code);
    const bit = KEY_BUTTONS[e.code];
    if (bit !== undefined) this.buttons |= bit;
  };

  private keyUp = (e: KeyboardEvent): void => {
    this.keys.delete(e.code);
    const bit = KEY_BUTTONS[e.code];
    if (bit !== undefined) this.buttons &= ~bit;
  };

  private releaseAll = (): void => {
    this.keys.clear();
    this.buttons = 0;
  };
}

function prevent(e: Event): void {
  e.preventDefault();
}
